
<template>
    <div
        class="mt-6 inline-flex w-full flex-col items-start justify-start gap-6 rounded-2xl bg-white p-6">
        <div class="w-full cursor-pointer gap-2 md:flex md:items-center md:justify-between">
            <div class="flex flex-col items-start md:gap-2 md:flex-row md:items-center">
                <vx-popover :hover="true"
                    :arrow="true"
                    placement="top">
                    <div class="max-w-sm">
                        <span :id="timeZoneAwareLabelId">Time-zone-aware start</span>
                        <button type="button"
                            class="ml-1 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                            aria-label="Time-zone-aware start details"
                            data-test="time-zone-aware-trigger">
                            <font-awesome-icon widthAuto :icon="faCircleInfo"
                                size="sm" />
                        </button>
                    </div>

                    <template v-slot:content>
                        <div class="max-w-sm">
                            <p v-if="!props.timeZoneAware"
                                class="text-sm text-slate-600">
                                This campaign will start based on your
                                <strong class="font-semibold">local time zone</strong>.
                                That means some contacts could receive the first message
                                at different time of day.
                            </p>

                            <div v-if="props.timeZoneAware"
                                class="space-y-6">
                                <div class="space-y-1">
                                    <h3 class="font-extrabold">Time-Zone-Aware Start</h3>

                                    <p class="text-sm">
                                        Campaign will start at
                                        {{ selectedDateTimeNewYork.format('h:mm a z') }}.
                                        The first message will be delayed to match
                                        {{ selectedDateTimeNewYork.format('h:mm a') }} for the
                                        <strong class="font-semibold">contact’s time zone</strong>
                                        if possible.
                                        <br /> <br />
                                        Contacts must have an assigned time zone or Guess Contact Time Zone must be enabled in team settings.
                                    </p>
                                </div>
                                <div class="flex gap-2">
                                    <vx-button color="muted"
                                        size="xs"
                                        target="_blank"
                                        :href="'/settings/teams/' + teamId + '#/owner'">
                                        Configure Defaults
                                        <font-awesome-icon widthAuto :icon="faGear"></font-awesome-icon>
                                    </vx-button>

                                    <vx-button color="muted"
                                        size="xs"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href="https://explore.voxie.com/voxiesupport/hc/en-us/articles/campaign-sending-by-timezone-optional">
                                        Documentation
                                        <font-awesome-icon widthAuto :icon="faBook"></font-awesome-icon>
                                    </vx-button>
                                </div>
                            </div>
                        </div>
                    </template>
                </vx-popover>
            </div>

            <vx-toggle size="sm" class="mt-2 md:mt-0" :disabled="props.disabled"
                :aria-labelledby="timeZoneAwareLabelId"
                :modelValue="props.timeZoneAware"
                @update:modelValue="emit('update:timeZoneAware', $event)" />
        </div>

        <div class="flex w-full flex-col items-center">
            <div
                class="relative z-10 mx-auto inline-flex transform-gpu justify-center gap-1.5 rounded-full border border-sky-300 border-solid bg-sky-100 py-1.5 pl-2 pr-3">
                <div class="flex items-center justify-center">
                    <font-awesome-icon widthAuto :icon="faClock"
                        size="xs"
                        class="text-sky-700" />
                </div>

                <div class="leading-none text-sky-900">
                    {{
                        dayjs(selectedDateTime)
                            .tz(getClientTz())
                            .format('h:mm a')
                    }}
                    &rarr; local time
                </div>
            </div>

            <div
                class="ml-0.5 h-4 border-l-2 border-sky-700 border-solid border-r-0 border-y-0 transition delay-300 duration-300 will-change-transform">
            </div>
            <figure class="relative z-0 w-full space-y-4">
                <div class="space-y-1">
                    <div v-for="(zone, index) in displayZones(selectedDateTime)"
                        :key="zone.value"
                        :style="{
                            'transition-delay': `${(displayZones.length - index) * 0.03}s`,
                        }"
                        class="relative inline-flex w-full items-center justify-start gap-2 overflow-clip rounded-lg border border-solid border-sky-300 bg-white px-4 py-3">
                        <div class="z-20 flex items-center gap-1 text-xs text-sky-800"
                            :class="dayjs(selectedDateTime).tz(zone.zone).format('z') ===
                                dayjs(selectedDateTime)
                                    .tz(getClientTz())
                                    .format('z')
                                ? 'font-semibold'
                                : ''
                                "
                            :title="dayjs(selectedDateTime).tz(zone.zone).format('zzz')
                                ">
                            <font-awesome-icon widthAuto class="text-sky-700"
                                :icon="faClock"
                                size="xs"
                                v-if="dayjs(selectedDateTime)
                                    .tz(zone.zone)
                                    .format('z') ===
                                    dayjs(selectedDateTime)
                                        .tz(getClientTz())
                                        .format('z')
                                    " />
                            {{ zone.label }} ({{ zone.value }})
                        </div>

                        <div class="background-repeat safe-sending-background absolute inset-be-0 inset-s-0 inset-bs-0 z-0 w-[200%] bg-blend-screen"
                            :style="{
                                backgroundSize: '50%, 6px 6px',
                                backgroundPositionX: `${backgroundOffset(selectedDateTime, zone.utcOffset, zone.zone, props.timeZoneAware)}%`,
                            }"></div>

                        <div class="absolute inset-be-0 inset-bs-0 z-20 -ml-4 inline-flex w-full items-center gap-2 border-sky-700 px-2 text-sky-700 transition duration-[.4s] ease-[cubic-bezier(0.35,1.3,0.5,1)] border-r-0 border-y-0"
                            :class="props.timeZoneAware &&
                                timeHasPassed(
                                    selectedDateTime,
                                    zone.zone,
                                ) &&
                                dayjs(selectedDateTime).tz(zone.zone).format('z') !== dayjs(selectedDateTime).tz(getClientTz()).format('z')
                                ? 'border-l-2 border-dashed opacity-50'
                                : 'border-l-2 border-solid'
                                "
                            :style="{
                                transform: props.timeZoneAware
                                    ? `translateX(${markerOffset(selectedDateTime, zone.utcOffset, zone.zone, props.timeZoneAware)}%)`
                                    : 'translateX(50%)',
                            }">
                            <template v-if="props.timeZoneAware">
                                <template
                                    v-if="timeHasPassed(selectedDateTime, zone.zone)">
                                    Now
                                    <font-awesome-icon widthAuto :icon="faArrowRightLongToLine"
                                        size="xs" />
                                </template>
                                <template v-else>
                                    {{ dayjs(selectedDateTime).tz(getClientTz()).format('h:mm a') }}
                                </template>
                            </template>
                            <template v-else>
                                {{ dayjs(selectedDateTime).tz(zone.zone).format('h:mm a') }}
                            </template>
                        </div>
                    </div>
                </div>

                <figcaption class="text-xs text-slate-600">
                    <dl class="grid grid-cols-[16px_minmax(0,1fr)] gap-y-2">
                        <dt class="border-l-2 border-sky-700 border-r-0 border-y-0 border-solid" />
                        <dd>Campaign start time</dd>

                        <dt
                            class="border-l-2 border-dashed border-sky-700 opacity-50 border-r-0 border-y-0" />
                        <dd>Time passed, immediate start</dd>

                        <dt class="safe-sending-key bg[left:6px_6px] background-repeat h-full w-2" />
                        <dd>Outside safe sending hours</dd>
                    </dl>
                </figcaption>
            </figure>
        </div>
    </div>
</template>

<script setup>
import { computed, useId } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faClock,
    faArrowRightLongToLine,
    faGear,
    faBook,
} from '@fortawesome/pro-solid-svg-icons'
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons'
import {
    VxButton,
    VxPopover,
    VxToggle
} from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs'
import { backgroundOffset, markerOffset, timeHasPassed, displayZones } from './timeZoneUtils'
import { getClientTz } from '~/utils/date'

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    timeZoneAware: {
        type: Boolean,
        default: false,
    },
    liveAt: {
        type: Date,
    }
});

const selectedDateTime = computed(() => props.liveAt || new Date());
const selectedDateTimeNewYork = computed(() => {
    const selectedDayjs = dayjs.tz(selectedDateTime.value, getClientTz());

    return selectedDayjs.clone().tz('America/New_York')
        .set('hours', selectedDayjs.get('hours'))
        .set('minutes', selectedDayjs.get('minutes'));
})

const teamId = Spark.state.currentTeam.id;

const emit = defineEmits(['update:timeZoneAware']);

const timeZoneAwareLabelId = useId();
</script>

<style scoped>
@reference "@css/style.css";

.vx-date-picker {
    @apply flex justify-center;
}

.safe-sending-background {
    /*
  safe sending 8am - 9pm, however display as 8pm due to staggering and queuing.
  each hour = 4.1667%
  midnight -> 0%
  8am -> 33.3%
  8pm -> 83.33%
  midnight -> 100%
  */

    background-image: linear-gradient(90deg,
    rgba(115, 216, 252, 0.4) 0%,
    rgba(115, 216, 252, 0.4) 29.3%,
    rgba(255, 255, 255, 0.4) 29.3%,
    rgba(255, 255, 255, 0.4) 79.55%,
    rgba(115, 216, 252, 0.4) 79.55%,
    rgba(115, 216, 252, 0.4) 100%),
    url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E");
    background-blend-mode: overlay;
}

.safe-sending-key {
    background-image: linear-gradient(90deg,
    rgba(115, 216, 252, 0.4) 0%,
    rgba(115, 216, 252, 0.4) 100%),
    url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E");
    background-blend-mode: overlay;
}
</style>
